import { Helmet } from "react-helmet-async";

interface SEOProps {
	title: string;
	description: string;
	keywords?: string;
	image?: string;
	url?: string;
	type?: string;
	author?: string;
	publishedTime?: string;
	modifiedTime?: string;
	canonical?: string;
	lang?: string;
	alternateLanguages?: Array<{ lang: string; url: string }>;
}

const AppSEO = ({
	title,
	description,
	keywords,
	image = "/images/atf-og-image.jpg",
	url,
	type = "website",
	author = "ATF Altecflex",
	publishedTime,
	modifiedTime,
	canonical,
	lang = "en",
	alternateLanguages = [],
}: SEOProps) => {
	const siteUrl = "https://www.altecflex.com";
	const currentUrl = url || canonical || window.location.href;
	const fullImageUrl = image.startsWith("http") ? image : `${siteUrl}${image}`;

	// Map language codes to proper og:locale format
	const getOGLocale = (language: string): string => {
		const localeMap: Record<string, string> = {
			en: "en_US",
			th: "th_TH",
		};
		return localeMap[language] || "en_US";
	};

	return (
		<Helmet>
			{/* HTML lang attribute */}
			<html lang={lang} />

			{/* Primary Meta Tags */}
			<title>{title}</title>
			<meta name="title" content={title} />
			<meta name="description" content={description} />
			{keywords && <meta name="keywords" content={keywords} />}
			<meta name="author" content={author} />
			
			{/* Canonical URL */}
			{canonical && <link rel="canonical" href={canonical} />}

			{/* hreflang alternates for multilingual SEO */}
			{alternateLanguages.length > 0 && (
				<>
					{alternateLanguages.map((alternate) => (
						<link
							key={alternate.lang}
							rel="alternate"
							hrefLang={alternate.lang}
							href={alternate.url}
						/>
					))}
				</>
			)}

			{/* Open Graph / Facebook */}
			<meta property="og:type" content={type} />
			<meta property="og:url" content={currentUrl} />
			<meta property="og:title" content={title} />
			<meta property="og:description" content={description} />
			<meta property="og:image" content={fullImageUrl} />
			<meta property="og:image:width" content="1200" />
			<meta property="og:image:height" content="630" />
			<meta property="og:site_name" content="ATF Altecflex" />
			<meta property="og:locale" content={getOGLocale(lang)} />
			{publishedTime && <meta property="article:published_time" content={publishedTime} />}
			{modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}

			{/* Twitter Card */}
			<meta name="twitter:card" content="summary_large_image" />
			<meta name="twitter:url" content={currentUrl} />
			<meta name="twitter:title" content={title} />
			<meta name="twitter:description" content={description} />
			<meta name="twitter:image" content={fullImageUrl} />

			{/* SEO for AI Crawlers */}
			<meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
			<meta name="googlebot" content="index, follow" />
			<meta name="bingbot" content="index, follow" />
			
			{/* Additional meta for better AI understanding */}
			<meta property="article:author" content={author} />
			<meta name="theme-color" content="#0066CC" />
		</Helmet>
	);
};

export default AppSEO;